/**
 * The rules of applying for an account: what the details panel needs before
 * it may be sent, what the request looks like, and how the code panel reads
 * what was pasted into it.
 *
 * An applicant has no session and no account, so nothing here can lean on
 * `me`; the address is proven by the code that is sent to it, and everything
 * else is the applicant's own word. The stage machine and the three API calls
 * are `useSignupController.ts`'s; the words, the countries and the lengths
 * are `signup.constants.ts`'s.
 *
 * `?invite=` changes one field and carries no privilege. An invited applicant
 * declares nothing, and faking the parameter earns exactly what an honest
 * invitation does — see `SignupKind`.
 */

import { normaliseDeclaredTypes } from "@ondo/shared";
import type { DeclaredMemberType, StartVerificationRequest } from "@ondo/shared";

import { ApiError } from "../lib/api.ts";
import { MEMBERSHIP_TYPES } from "../lib/constants.ts";
import {
  CODE_LENGTH,
  COUNTRIES,
  MIN_PASSWORD_LENGTH,
  SIGNUP_COPY,
} from "./signup.constants.ts";

/** Everything the first panel collects, exactly as typed. */
export interface DetailsValues {
  fullName: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
  country: string;
  /** One or more — people are often several of these at once. */
  declaredTypes: DeclaredMemberType[];
  /** Null until the applicant answers; the question is not pre-answered for them. */
  bookCall: boolean | null;
}

export const EMPTY_DETAILS: DetailsValues = {
  fullName: "",
  email: "",
  phone: "",
  password: "",
  confirmPassword: "",
  country: COUNTRIES[0],
  declaredTypes: ["collector"],
  bookCall: null,
};

export interface SummaryRow {
  label: string;
  value: string;
}

/**
 * Why the details cannot be submitted, or null if they can.
 *
 * Deliberately one message rather than per-field errors: the panel is short
 * enough to scan, and a single line under the form keeps the layout from
 * jumping as fields are corrected.
 *
 * The email rule is intentionally loose. Anything stricter rejects real
 * addresses — and it does not need to be strict, because the address is about
 * to be proven by sending a code to it.
 */
/**
 * What kind of sign-up this is.
 *
 * `invited` arrives from `/apply?invite=<address>`: somebody asked this address
 * to confirm a provenance entry, so an account is already waiting to be claimed.
 * The difference it makes is one field — an invited applicant declares nothing,
 * because a haulier, a registrar or a shipping clerk is none of the eight things
 * the form offers and picking one to get past it would be worse data than none.
 *
 * NOTHING SECURITY-BEARING HANGS ON THIS. The parameter is in the URL and a
 * client can set it at will, which is fine: declaring nothing is not a
 * privilege, and `ondo.provision_signup()` adopts on the address the email code
 * PROVED rather than on anything the form says. Faking `?invite=` gets you an
 * account with an empty `declared_types` and no workspace — which is exactly
 * what an honest invited applicant gets, and which `PUT /v1/me/declared-types`
 * can correct afterwards for either of them.
 */
export type SignupKind = "open" | "invited";

export function validateDetails(
  values: DetailsValues,
  kind: SignupKind = "open",
): string | null {
  const required = [
    values.fullName,
    values.email,
    values.password,
    values.phone,
    values.country,
  ];
  if (required.some((field) => field.trim() === "")) return SIGNUP_COPY.incomplete;

  if (!looksLikeEmail(values.email)) return SIGNUP_COPY.emailInvalid;
  if (values.password !== values.confirmPassword) return SIGNUP_COPY.passwordMismatch;
  if (values.password.length < MIN_PASSWORD_LENGTH) return SIGNUP_COPY.passwordTooShort;
  // Reachable by unticking the default rather than by never choosing, which is
  // why it is checked at all — the group starts with one selected. An invited
  // applicant is never shown the group, so there is nothing to have unticked.
  if (kind === "open" && values.declaredTypes.length === 0) {
    return SIGNUP_COPY.membershipRequired;
  }

  return null;
}

/**
 * The address `/apply?invite=` names, or null.
 *
 * Read from `location.search` rather than taken as a prop, for the reason
 * `intendedRole` is read the way it is: the wizard is mounted by a route that
 * does not otherwise care, and threading a parameter through it would make
 * every caller know about invitations.
 *
 * VALIDATED, THEN PREFILLED. A query string is arbitrary text, so anything that
 * is not an address shape is dropped and the applicant simply types their own —
 * which is also what happens to a mangled link. It is never trusted for
 * anything beyond what appears in the field: the email code proves the address,
 * and `ondo.provision_signup()` adopts on what auth.users holds afterwards.
 */
export function invitedAddress(search = globalThis.location?.search ?? ""): string | null {
  const raw = new URLSearchParams(search).get("invite");
  if (!raw) return null;

  const address = raw.trim().toLowerCase();
  return looksLikeEmail(address) ? address : null;
}

/** One @, something either side, a dot in the domain, and no spaces anywhere. */
function looksLikeEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

/**
 * True only when both password fields are filled and equal — drives the "✓
 * Passwords match" hint, which must not appear while both are still empty.
 */
export function passwordsMatch(values: DetailsValues): boolean {
  return values.password.length > 0 && values.password === values.confirmPassword;
}

/** The request body, with the shapes the API expects rather than the form's. */
export function toStartRequest(
  values: DetailsValues,
  identitySkipped: boolean,
  kind: SignupKind = "open",
): StartVerificationRequest {
  const declaredTypes = normaliseDeclaredTypes(values.declaredTypes);

  return {
    fullName: values.fullName.trim(),
    email: values.email.trim(),
    // Empty means "not given", not an empty string, so the column stays null.
    phone: values.phone.trim() === "" ? null : values.phone.trim(),
    country: values.country,
    // OMITTED, not sent empty. The two are the same sentence and the schema
    // accepts only one of them: `ondo.email_verifications.declared_types` still
    // refuses '{}' — see 20260802213100, which explains that an empty array
    // reaching ondo.people is indistinguishable from the null a pre-signup row
    // legitimately carries. An invited applicant is that null.
    //
    // The `kind` guard rather than a bare length check, so an OPEN sign-up that
    // somehow arrives with nothing ticked still sends the field and is refused
    // by the API, instead of quietly succeeding as an invited one.
    ...(kind === "invited" && declaredTypes.length === 0 ? {} : { declaredTypes }),
    bookCall: values.bookCall === true,
    identitySkipped,
  };
}

/** The confirmation table on the final panel. */
export function summaryRows(values: DetailsValues, ondoId: string): SummaryRow[] {
  return [
    { label: "Name", value: values.fullName.trim() },
    { label: "Email", value: values.email.trim() },
    { label: "ONDO ID", value: ondoId },
    { label: "Country", value: values.country },
    { label: "Membership", value: membershipLabels(values.declaredTypes) },
    {
      label: "Onboarding call",
      value: values.bookCall === true ? "Booked" : "Not requested",
    },
  ];
}

export function membershipLabel(value: DeclaredMemberType): string {
  return MEMBERSHIP_TYPES.find((type) => type.value === value)?.label ?? value;
}

/** "Collector, Gallery" — canonical order, so the summary is not click order. */
export function membershipLabels(values: readonly DeclaredMemberType[]): string {
  const labels = normaliseDeclaredTypes(values).map(membershipLabel);
  return labels.length === 0 ? "—" : labels.join(", ");
}

/** True for exactly CODE_LENGTH digits, ignoring whitespace around them. */
export function isCompleteCode(code: string): boolean {
  return new RegExp(`^[0-9]{${CODE_LENGTH}}$`).test(code.trim());
}

/**
 * Keeps only digits and clips to length, so a pasted "123 456" or a code
 * copied with its label still lands in the box.
 */
export function sanitiseCode(raw: string): string {
  return raw.replace(/[^0-9]/g, "").slice(0, CODE_LENGTH);
}

/** Whole seconds until an ISO timestamp, floored at zero. */
export function secondsUntil(iso: string, now: number): number {
  const target = Date.parse(iso);
  if (Number.isNaN(target)) return 0;
  return Math.max(0, Math.ceil((target - now) / 1000));
}

/**
 * What to show the applicant when a call fails.
 *
 * The API's own message is preferred because it is the one written for this
 * situation — "That code is not right", "Too many signup attempts". Anything
 * else, including a bare network TypeError, becomes generic copy rather than
 * leaking an exception string into the panel.
 */
export function describeError(error: unknown): string {
  if (error instanceof ApiError) {
    return error.message.trim() === "" ? SIGNUP_COPY.unknownError : error.message;
  }
  return SIGNUP_COPY.unknownError;
}
